import {DOCUMENT_TYPE, TASK_ACTIONS} from "utils/constants";
import {MyTasksActions} from "./AvailableActions.class";
import type {MyTask, TaskWrapper} from "types/myTasks";

/**
 * Grouping of the my-tasks list into sections.
 * Pure functions so they can be unit-tested without the component.
 */

export const GROUP_BY = Object.freeze({
    URGENCY: 'urgency',
    COMPANY: 'company',
    FROM: 'from',
    DOCUMENT_TYPE: 'documentType'
});

export interface TaskGroup {
    key: string;
    label?: string;
    labelKey?: string;
    wrappers: TaskWrapper[];
}

const DAY = 86400000;

// urgency bucket keys, in the order they should be shown
export const URGENCY_BUCKETS: string[] = ['overdue', 'today', 'week', 'later', 'none'];

function startOfDay(ts: number | string): number {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

/**
 * bucket a task by document due date relative to `now`
 */
export function getUrgencyBucket(task: MyTask, now: number = Date.now()): string {
    const due = task.documentDueDate;
    if (due == null) return 'none';
    const today0 = startOfDay(now);
    const due0 = startOfDay(due);
    if (due0 < today0) return 'overdue';
    if (due0 === today0) return 'today';
    if (due0 <= today0 + 7 * DAY) return 'week';
    return 'later';
}

/**
 * the "From" value, matching what the old list derived
 */
export function fromValue(task: MyTask): string {
    if (!task) return '';
    if (task.supplierName) return task.supplierName;
    if (task.requesterName) return task.requesterName;
    if (task.requesterFirstName) return (task.requesterFirstName + ' ' + (task.requesterLastName || '')).trim();
    return '';
}

/**
 * group by a value read off the task, sections in alphabetical key order
 */
function groupByValue(wrappers: TaskWrapper[], valueOf: (task: MyTask) => string): TaskGroup[] {
    const byKey = new Map<string, TaskWrapper[]>();
    wrappers.forEach(w => {
        const key = valueOf(w.task) || '';
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(w);
    });
    return [...byKey.keys()]
        .sort((a, b) => a.localeCompare(b))
        .map(key => ({key, wrappers: byKey.get(key)}));
}

/**
 * group the already filtered+sorted task wrappers into sections.
 * Order inside each group is preserved (respects the current sort).
 */
export function groupWrappers(wrappers: TaskWrapper[], groupBy: string, now: number = Date.now()): TaskGroup[] {
    if (groupBy === GROUP_BY.COMPANY) {
        return groupByValue(wrappers, t => t.companyName).map(g => ({...g, label: g.key}));
    }

    if (groupBy === GROUP_BY.FROM) {
        return groupByValue(wrappers, fromValue).map(g => ({...g, label: g.key}));
    }

    if (groupBy === GROUP_BY.DOCUMENT_TYPE) {
        return groupByValue(wrappers, t => DOCUMENT_TYPE.asString(t.documentType) || 'UNKNOWN_TYPE')
            .map(g => ({...g, labelKey: 'documentType.' + g.key}));
    }

    const buckets: Record<string, TaskWrapper[]> = {};
    URGENCY_BUCKETS.forEach(k => (buckets[k] = []));
    wrappers.forEach(w => buckets[getUrgencyBucket(w.task, now)].push(w));
    return URGENCY_BUCKETS
        .filter(k => buckets[k].length > 0)
        // the translation keys keep the overview namespace they were first shipped under
        .map(k => ({key: k, labelKey: 'myTasks.overview.bucket.' + k, wrappers: buckets[k]}));
}

/**
 * keys of the tasks in the list that currently allow the APPROVE action
 */
export function approvableKeys(wrappers: TaskWrapper[]): string[] {
    return wrappers
        .filter(w => new MyTasksActions(w.task).isEnabled(TASK_ACTIONS.APPROVE))
        .map(w => w.task.key);
}

export const GROUP_BY_STORAGE_KEY = '@@myTasks/groupBy';

const GROUP_BY_VALUES: readonly string[] = Object.freeze(Object.values(GROUP_BY));

/**
 * the chosen grouping survives leaving the page - opening a task and coming back included;
 * anything unreadable just means "group the way the list opens"
 */
export function readStoredGroupBy(storage: Storage = window.localStorage): string {
    try {
        const raw = storage.getItem(GROUP_BY_STORAGE_KEY);
        return raw && GROUP_BY_VALUES.includes(raw) ? raw : GROUP_BY.URGENCY;
    } catch (e) {
        return GROUP_BY.URGENCY;
    }
}

export function writeStoredGroupBy(groupBy: string, storage: Storage = window.localStorage): void {
    try {
        storage.setItem(GROUP_BY_STORAGE_KEY, groupBy);
    } catch (e) {
        // a full or blocked storage must not break the grouping
    }
}
